<template>
  <form id="invoice-form" ref="invoiceForm" @submit.prevent="handleSubmit">
    <invoice-form-section
      title="Enter your company's information"
      :currentStep=1
      :totalSteps=3
    >
      <invoice-form-contact-fields
        :contact="this.form.contacts[0]"
      />
      <invoice-form-image-upload
        @imageCreated="imageCreated"
      />
    </invoice-form-section>
    <invoice-form-section
      title="Enter customer and invoice information"
      :currentStep=2
      :totalSteps=3
    >
      <invoice-form-contact-fields
        :contact="this.form.contacts[1]"
      />
      <invoice-form-information-fields
        :form="this.form"
      />
    </invoice-form-section>
    <invoice-form-section
      title="Enter the line items you wish to bill"
      :currentStep="3"
      :totalSteps="3"
    >
      <invoice-form-line-items
        :items="this.form.items"
        @addLineItem="this.addLineItem"
        @removeLineItem="this.removeLineItem"
      />
    </invoice-form-section>
    <invoice-form-footer
      :form="this.form"
      :subtotal="this.subtotal"
      :tax="this.tax"
      :total="this.total"
    />
    <section class="invoice-form-actions">
      <button type="submit" class="button submit-button">Generate PDF Invoice</button>
    </section>
  </form>
</template>

<script>
import JsPdf from 'jspdf'
import 'jspdf-autotable'
import InvoiceFormSection from './InvoiceFormSection.vue'
import InvoiceFormContactFields from './InvoiceFormContactFields.vue'
import InvoiceFormImageUpload from './InvoiceFormImageUpload.vue'
import InvoiceFormInformationFields from './InvoiceFormInformationFields.vue'
import InvoiceFormLineItems from './InvoiceFormLineItems.vue'
import InvoiceFormFooter from './InvoiceFormFooter.vue'

export default {
  name: 'InvoiceForm',
  components: {
    InvoiceFormSection,
    InvoiceFormContactFields,
    InvoiceFormImageUpload,
    InvoiceFormInformationFields,
    InvoiceFormLineItems,
    InvoiceFormFooter
  },
  data () {
    return {
      form: {
        contacts: [
          {
            id: 0,
            type: 'Company',
            name: '',
            email: '',
            address: '',
            city: '',
            zip: '',
            country: '',
            region: '',
            image: ''
          },
          {
            id: 1,
            type: 'Customer',
            name: '',
            email: '',
            address: '',
            city: '',
            zip: '',
            country: '',
            region: ''
          }
        ],
        items: [
          {
            id: 1,
            itemName: '',
            description: '',
            quantity: 0,
            price: 0
          }
        ],
        nextItemId: 2,
        invoiceNumber: '1',
        invoiceDate: '',
        notes: '',
        deductions: 0,
        taxRate: 0
      }
    }
  },

  computed: {
    subtotal: function () {
      return this.form.items.map(item => item.price * item.quantity).reduce((total, amount) => total + amount)
    },
    tax: function () {
      return Number(this.subtotal) * Number(this.form.taxRate) * 0.01
    },
    total: function () {
      if (this.subtotal && this.tax) {
        return Number(this.subtotal) + Number(this.tax)
      } else if (this.subtotal && this.tax && this.form.deductions) {
        return Number(this.subtotal) + Number(this.tax) - Number(this.form.deductions)
      } else {
        return 0
      }
    }
  },

  methods: {
    imageCreated (value) {
      this.form.contacts[0].image = value
    },
    addLineItem () {
      this.form.items.push({
        id: this.form.nextItemId,
        itemName: '',
        description: '',
        quantity: 0,
        price: 0
      })
      this.form.nextItemId += 1
    },
    removeLineItem (idToRemove) {
      this.form.items = this.form.items.filter(item => {
        return item.id !== idToRemove
      })
    },
    handleSubmit () {
      console.log(this.$refs.invoiceForm)

      var doc = new JsPdf()

      var invoiceNumber = (this.form.invoiceNumber ? this.form.invoiceNumber : '')
      var invoiceDate = (this.form.invoiceDate ? this.form.invoiceDate : '')

      var company = this.form.contacts[0]
      var companyLogo = company.image && company.image
      // Add the comma after companyCity here
      // If no companyCity, don't show comma
      var companyCity = (company.city ? company.city + ', ' : '')
      var companyAddressLineTwo = company.city + company.region + ' ' + company.zip
      var companyAddressBlock = company.name + '\n' + company.address + '\n' + companyAddressLineTwo + '\n' + company.country + '\n' + company.email

      var client = this.form.contacts[1]
      // Add the comma after clientCity here
      // If no clientCity, don't show comma
      var clientCity = (client.city ? client.city + ', ' : '')
      var clientAddressLineTwo = client.city + client.region + ' ' + client.zip
      var clientAddressBlock = client.name + '\n' + client.address + '\n' + clientAddressLineTwo + '\n' + client.country + '\n' + client.email

      var lineItems = this.form.items.map((item) => {
        var price = Number(item.price).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
        var itemTotal = Number(item.price * item.quantity).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
        return [String(item.itemName) + '\n' + String(item.description), String(item.quantity), price, itemTotal ]
      })

      // Default subtotal = 0
      var subtotal = 0
      // If there are lineItems in the form
      // calculate sub-total from lineItems
      if (lineItems.length > 0) {
        subtotal = this.form.items.map(item => item.price * item.quantity).reduce((total, amount) => total + amount)
      }

      // Default tax = 0
      var tax = 0
      // If there is a subtotal
      // calculate the tax amount
      if (subtotal) {
        tax = subtotal * this.form.taxRate * 0.01
      }

      // Default deductions = 0, otherwise this.form.deductions
      var deductions = (this.form.deductions ? this.form.deductions : 0)

      // Calculate the total
      // parseInt to ensure integers
      total = parseInt(subtotal) + parseInt(tax) + parseInt(deductions)

      var notes = this.form.notes

      // Add company image to top left corner of the invoice
      if (companyLogo) {
        doc.addImage(companyLogo, 'JPEG', 15, 15, 15, 15)
      }

      // Add company and client information blocks
      doc.autoTable({
        startY: 40,
        columnStyles: {
          2: {
            halign: 'center'
          },
          3: {
            halign: 'right'
          }
        },
        body: [
          [companyAddressBlock, '', '', clientAddressBlock],
          ['Invoice #' + invoiceNumber + '\n' + invoiceDate, '', '', '']
        ],
        theme: 'plain'
      })

      doc.autoTable({
        startY: doc.autoTable.previous.finalY + 15,
        headStyles: {
          2: {
            halign: 'center'
          },
          3: {
            halign: 'right'
          }
        },
        columnStyles: {
          2: {
            halign: 'center'
          },
          3: {
            halign: 'right'
          }
        },
        footStyles: {
          halign: 'right'
        },
        head: [['Task & Description', 'Quantity', 'Price', 'Total']],
        body: lineItems,
        foot: [
          ['', '', 'Subtotal', '$' + subtotal],
          ['', '', 'Tax', '$' + tax],
          ['', '', 'Deductions', '$' + deductions],
          ['', '', 'Total', '$' + total]
        ],
        theme: 'plain'
      })

      doc.autoTable({
        startY: doc.autoTable.previous.finalY,
        head: [['Memo / Notes']],
        body: [[notes]],
        theme: 'plain'
      })

      doc.save('invoice.pdf')
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '../../styles/Variables.scss';

  .invoice-form-actions {
    display: flex;
    justify-content: flex-end;
    .submit-button {
      display: block;
      padding: 1.0625rem 1.875rem;
      border-radius: 5px;
      font-weight: 700;
      font-size: 1rem;
      line-height: 1.133;
      transition: 150ms ease;
      transition-property: background-color, border-color, box-shadow, color;
      text-align: center;
      user-select: none;
      box-shadow: 0 5px 15px 0 rgba(0,0,0,0.15);
      background-color: $blue-dream;
      color: white;
      border-width: 0;
      border-style: solid;
      border-color: transparent;
      &:hover {
        background-color: $la-confidential;
      }
    }
  }
</style>
